<template>
    <SidebarSection v-if="userHasStandardServiceLevel && profileViews && getSelectedIdentity" class="profile-views-status">
        <span class="profile-views-status__title">
            {{ $t('search.service-level.profile-views.status.title') }}
        </span>
        <VJoyWrapper direction="row" item-stretch no-margin>
            <VJoyProgressBar
                class="profile-views-status__progress-bar"
                :color="status.progressBarColor"
                :current-step="profileViews.count"
                :steps="profileViews.limit"
            />
            <VJoyBadge
                class="profile-views-status__progress-number"
                :variant="status.badgeVariant"
                :bulletpoint="false"
                data-testid="profile-views-progress-text-value"
            >
                {{ profileViews.count }} / {{ profileViews.limit }}
            </VJoyBadge>
        </VJoyWrapper>
        <a
            class="joy-link"
            :href="`/client-settings/organization/${getSelectedIdentity.organizationId!}/service-level?pageSource=blocker&successRedirectPath=${currentRouteUrlEncoded}&successRedirectPath=${currentRouteUrlEncoded}`"
        >
            {{ $t('search.service-level.profile-views.status.upgrade-link') }}
        </a>
    </SidebarSection>
</template>

<script setup lang="ts">
    import {computed, onBeforeUnmount, onMounted} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useRoute} from 'vue-router';
    import {useAuth} from '#imports';
    import {type TJoyBadgeVariants, type TJoyProgressBarColors, VJoyBadge, VJoyWrapper, VJoyProgressBar} from '@maltjoy/core-vue';
    import SidebarSection from '~/components/common/SidebarSection.vue';
    import {useUserInfo} from '~/composables/useUserInfo';
    import {useProfileViews} from './useProfileViews';

    const route = useRoute();
    const {serviceLevel} = useUserInfo();
    const {profileViewsStatus: profileViews, updateProfileViewsStatus} = useProfileViews();

    const userHasStandardServiceLevel = computed(() => serviceLevel.value === 'STARTER');
    const currentRouteUrlEncoded = computed(() => encodeURIComponent(route.fullPath));

    const {getSelectedIdentity} = storeToRefs(useAuth());

    const status = computed<{
        progressBarColor: TJoyProgressBarColors;
        badgeVariant: TJoyBadgeVariants;
    }>(() => {
        if (profileViews.value) {
            const usage = profileViews.value.count / profileViews.value.limit;
            if (usage >= 0.9) {
                return {
                    progressBarColor: 'error',
                    badgeVariant: 'red',
                };
            } else if (usage >= 0.75) {
                return {
                    progressBarColor: 'warning',
                    badgeVariant: 'orange',
                };
            }
        }
        return {
            progressBarColor: 'success',
            badgeVariant: 'green',
        };
    });

    onMounted(() => {
        window.addEventListener('focus', updateProfileViewsStatus);
    });

    onBeforeUnmount(() => {
        window.removeEventListener('focus', updateProfileViewsStatus);
    });
</script>

<style scoped lang="scss">
    .profile-views-status {
        font-size: var(--joy-font-size-primary-300);

        a {
            font-weight: var(--joy-font-weight-normal);
        }

        &__title {
            font-weight: var(--joy-font-weight-bold);
        }

        &__progress-bar {
            width: inherit;
        }

        &__progress-number {
            flex-grow: 0;
        }
    }
</style>
